import {
  ADDON_PRODUCT_BASE_TYPE,
  ADDON_PRODUCT_DWH_TYPE,
  ADDON_PRODUCT_METABASE_AI_TIERED_TYPE,
  ADDON_PRODUCT_TRANSFORMS_ADVANCED_METERED_TYPE,
  ADDON_PRODUCT_TRANSFORMS_ADVANCED_TYPE,
  ADDON_PRODUCT_TRANSFORMS_BASIC_METERED_TYPE,
  ADDON_PRODUCT_TRANSFORMS_BASIC_TYPE,
} from "@stores/addons/constants";
import {
  ISubscription,
  AddonSubscribedProductType,
  SubscribedProductsType,
} from "./types";
import { SUBSCRIPTION_STATUS_TRIAL } from "./constants";

export const isSubscriptionInTrial = ({
  in_trial,
  status,
}: Pick<ISubscription, "in_trial" | "status">) => {
  return in_trial || status === SUBSCRIPTION_STATUS_TRIAL;
};

export function getAddonSubscribedProducts(
  subscribedProducts: SubscribedProductsType,
): AddonSubscribedProductType[] {
  return subscribedProducts.filter(
    (subscribedProduct) =>
      subscribedProduct.product_type !== ADDON_PRODUCT_BASE_TYPE,
  ) as AddonSubscribedProductType[];
}

const createFindAddOnByProductType =
  (productType: AddonSubscribedProductType["product_type"]) =>
  (subscription: Pick<ISubscription, "subscribed_products">) =>
    subscription.subscribed_products.find(
      (addOn) => addOn.product_type === productType,
    ) as AddonSubscribedProductType | undefined;

export const isAddOnInTrial = (
  { trial_ends_at }: Pick<AddonSubscribedProductType, "trial_ends_at">,
  subscription: Pick<ISubscription, "in_trial" | "status">,
) =>
  isSubscriptionInTrial(subscription) ||
  (!!trial_ends_at && new Date(trial_ends_at) > new Date());

export const getAddOnTrialDate = (
  addOn: Pick<AddonSubscribedProductType, "trial_ends_at">,
  subscription: Pick<ISubscription, "in_trial" | "status" | "trial_ends_at">,
) =>
  isAddOnInTrial(addOn, subscription)
    ? addOn.trial_ends_at || subscription.trial_ends_at
    : undefined;

/**
 * prepaid storage add-on
 */
export const getSubscriptionStorageAddOn = createFindAddOnByProductType(
  ADDON_PRODUCT_DWH_TYPE,
);

export const getMetabotAddOn = createFindAddOnByProductType(
  ADDON_PRODUCT_METABASE_AI_TIERED_TYPE,
);

export function getTransformsAddOn({
  subscribed_products,
}: Pick<ISubscription, "subscribed_products">):
  | AddonSubscribedProductType
  | undefined {
  const transforms = getAddonSubscribedProducts(subscribed_products).filter(
    ({ product_type }) =>
      product_type === ADDON_PRODUCT_TRANSFORMS_BASIC_TYPE ||
      product_type === ADDON_PRODUCT_TRANSFORMS_ADVANCED_TYPE ||
      product_type === ADDON_PRODUCT_TRANSFORMS_BASIC_METERED_TYPE ||
      product_type === ADDON_PRODUCT_TRANSFORMS_ADVANCED_METERED_TYPE,
  );

  // first transforms add-on only
  return transforms.length ? transforms[0] : undefined;
}

interface IPayAsYouGoMeterValues {
  billableUnits: number;
  includedUnitsUsed: number;
  /** nothing left to draw down: every further unit bills */
  areFreeUnitsConsumed: boolean;
}

interface IPayAsYouGoMeterValuesParams {
  /** raw meter reading; `null`/`undefined` means the meter has not reported */
  meterValue: number | null | undefined;
  freeUnits: number;
  meterFreeUnits: number | null | undefined;
}

// Harbormaster contract for `pay_as_you_go` metering (`update-meter`,
// `period-free-units`, Stripe sync) — shared by `metabase-ai-managed` and
// `product-analytics`:
// - `meterValue`: MPU sum for the current billing period.
// - `freeUnits`: lifetime free allowance (cross-period pool on the addon).
// - `meterFreeUnits`: remaining free pool allocatable against this period,
//   `max(0, freeUnits − usage_before_this_period)`. Billable usage is
//   `meterValue − meterFreeUnits` (floored at 0).
//
// Lifetime draw on the free pool is `meterValue + freeUnits − meterFreeUnits`
// when `meterFreeUnits` is set; included use for the graph is that draw capped
// at `freeUnits`.
//
// `meterFreeUnits` may be `null` before Harbormaster materializes meter fields;
// we treat that like first-period semantics (no prior draw on the pool) for the
// split, but as "every recorded unit bills" for `areFreeUnitsConsumed` — the
// billing side has no pool to draw from until it exists.
//
// Note this does not apply to `dwh-rent`, whose `meter_strategy` is `rent`: it
// bills the live footprint against `included_units` instead.
export function getPayAsYouGoMeterValues({
  meterValue: rawMeterValue,
  freeUnits,
  meterFreeUnits,
}: IPayAsYouGoMeterValuesParams): IPayAsYouGoMeterValues {
  const meterValue = rawMeterValue ?? 0;
  // no prior draw on the pool when Harbormaster has not materialized it yet
  const remainingFreePool = meterFreeUnits ?? freeUnits;
  const hasReported = typeof rawMeterValue === "number";

  return {
    billableUnits: Math.max(0, meterValue - remainingFreePool),
    includedUnitsUsed: Math.max(
      0,
      Math.min(freeUnits, meterValue + freeUnits - remainingFreePool),
    ),
    areFreeUnitsConsumed:
      typeof meterFreeUnits === "number"
        ? meterFreeUnits <= 0 || (hasReported && meterValue >= meterFreeUnits)
        : hasReported,
  };
}

export function getAddonsPrice({
  subscribed_products,
}: Pick<ISubscription, "subscribed_products">): number {
  const prices = getAddonSubscribedProducts(subscribed_products).map(
    ({ base_fee, total_units, included_units, price_per_unit, is_metered }) => {
      if (is_metered) {
        return base_fee;
      }
      return base_fee + (total_units - included_units) * price_per_unit;
    },
  );

  return prices.length ? prices.reduce((acc, price) => acc + price) : 0;
}
